import { z } from 'zod'

import { PersonDisplay } from '@/models/features/display-column.models'

import type { BusinessHour } from './packages.models'

export const MaintenanceStatusEnum = z.enum([
  'Pending',
  'In Progress',
  'On Hold',
  'Finished',
  'Canceled',
])
export type MaintenanceStatus = z.infer<typeof MaintenanceStatusEnum>

export const MaintenancePriorityEnum = z.enum([
  'Emergency',
  'Critical',
  'High',
  'Med',
  'Low',
  'Routine',
])
export type MaintenancePriority = z.infer<typeof MaintenancePriorityEnum>

// Note: '' (not undefined) means "all" - matches the tab filter's own option shape, see
// maintenance-dashboard.page.constants.ts.
export const MaintenanceTabTypeEnum = z.enum(['', 'pending', 'scheduled', 'completed'])
export type MaintenanceTabType = z.infer<typeof MaintenanceTabTypeEnum>

export type MaintenanceAssignee = {
  id: number
  phone?: string | number | null
  user: {
    firstName: string
    lastName: string
    email: string
    avatar: string | null
    role?: string | null
  }
}

// Note: named `clientId` (not `client`) to match the API response verbatim - Sails populates
// this field in place when the request has a resident attached, it isn't actually an id.
export type MaintenanceRequestClient = {
  id: number
  firstName: string
  lastName: string
  email: string
  avatar: string | null
  apartment: string | null
  street: string | null
  phone?: string | null
  buildingNumber?: string | null
  yardiPropertyId?: string | null
}

// Note: where a work order came from - mirrors the BE's `MaintenanceSource`. Natively created
// requests are 'ElevatedLiving' (older rows may have no source at all).
export type MaintenanceSource =
  | 'ElevatedLiving'
  | 'Yardi'
  | 'RealPage'
  | 'Entrata'
  | 'ResMan'
  | 'RentManager'
  | 'AppFolio'

// Note: ResMan work orders store their areas as a list - the BE parses it back from JSON. The
// entries have been seen both as plain names and as `{ name }` objects.
export type MaintenanceLocation = string | (string | { name?: string | null })[]

// Note: the user who created the work order, populated by the BE from the stored email. It stays
// the raw email string when no user matches, and is null for most PMS-synced work orders.
export type MaintenanceCreatedBy = {
  firstName: string
  lastName: string
  email: string
  avatar: string | null
  role: string
  deleted?: boolean
  clientId?: number | null
  phone?: string | null
  apartment?: string | null
  buildingNumber?: string | null
  street?: string | null
}

export type MaintenanceRequest = {
  // Note: the BE's per-column person details - only the fields enabled in its settings (EL-15064)
  createdByDisplay?: PersonDisplay
  assigneeDisplay?: PersonDisplay
  id: number
  updatedAt: number
  scheduledDate: number | null
  status: MaintenanceStatus
  priority: MaintenancePriority | null
  assignee: MaintenanceAssignee | null
  clientId: MaintenanceRequestClient | null
  buildingId: {
    id: number
    name: string
    address: string
  }
  unit?: string | null
  shortDescription?: string | null
  description?: string | null
  location?: MaintenanceLocation | null
  subcategory?: string | null
  category?: string | null
  source?: MaintenanceSource | null
  createdBy?: MaintenanceCreatedBy | string | null
}

export type MaintenanceStats = {
  // Note: the BE ignores the `status` filter for every count below (they still follow search,
  // dates, resident, etc.), so the status tabs stay stable while one of them is selected.
  // The per-status fields (EL-15063) are optional: a BE without them yet only sends the legacy
  // buckets - see getMaintenanceAllCount / getMaintenanceStatusCounts for the fallback.
  allCount?: number
  // Note: `pendingCount` is both the Pending status and the legacy "pending" bucket.
  pendingCount: number
  inProgressCount?: number
  onHoldCount?: number
  finishedCount?: number
  canceledCount?: number
  // Note: legacy bucket counts - the old-design page reads them, and they're the fallback total.
  scheduledCount: number
  completedCount: number
}

// Note: `GET`/`PUT /v2/maintenance/settings` share this shape. `slotLength` is in hours, with `0`
// meaning "not set yet"; business hours are minutes from midnight, only for enabled days.
export type MaintenanceSettings = {
  buildingId: number
  requiredAssignee: boolean
  requiredScheduledDate: boolean
  suggestedTimeframeEnabled: boolean
  slotLength: number
  businessHours: BusinessHour[]
}
